import { useMemo, useState } from "react";
import { AlertTriangle, Info } from "lucide-react";
import { groupIntoTurns } from "../executionTimeline";
import TurnCard from "./TurnCard";
import EntryList from "./EntryList";

// La pestaña de resumen: la ejecución contada por turnos de conversación.
// Un turno es una espera y el trabajo que corrió al destrabarse, así que un
// bot que respondió cuatro mensajes se lee como cuatro turnos y no como
// noventa y dos corridas seguidas.

const Notice = ({ icon: Icon, tone, title, children }) => (
  <div className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 ${tone}`}>
    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
    <div className="text-[11px] leading-relaxed">
      <span className="font-medium">{title}</span> {children}
    </div>
  </div>
);

const Legend = () => (
  <div className="flex flex-wrap items-center gap-4 border-t border-white/5 px-1 pt-3 text-[10px] text-gray-500">
    <span className="flex items-center gap-1.5">
      <span className="h-3 w-1.5 rounded-[2px] bg-emerald-500" /> step run
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-3 w-1.5 rounded-[2px] bg-rose-500" /> failed run
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-3 w-1.5 rounded-[2px] bg-blue-500" /> running
    </span>
    <span className="text-gray-600">
      One block is one run of a step, not a slice of time — durations are written out.
    </span>
  </div>
);

export default function ExecutionSummary({
  timeline,
  degraded,
  degradedReason,
  truncated,
  truncatedReason,
  onSelectEntry,
}) {
  const turns = useMemo(() => groupIntoTurns(timeline), [timeline]);
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (index) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  const allExpanded = turns.length > 0 && turns.every((turn) => expanded.has(turn.index));
  // Una ejecución con una sola espera igual esperó: el título se decide por
  // las esperas que hubo, no por cuántos turnos salieron.
  const neverWaited = turns.every((turn) => !turn.openingWait);
  const toggleAll = () =>
    setExpanded(allExpanded ? new Set() : new Set(turns.map((turn) => turn.index)));

  if (degraded) {
    return (
      <div className="space-y-3">
        <Notice
          icon={AlertTriangle}
          tone="border-amber-500/30 bg-amber-500/5 text-amber-200/90"
          title="Partial history."
        >
          This execution ran before per-run recording existed{degradedReason ? ` (${degradedReason})` : ""}.
          It shows one entry per step instead of one per run, without inputs, and only the
          durations that were actually measured. Each step carries the timestamps of its last
          pass, so it is not grouped into turns — the order between steps and waits would not
          hold.
        </Notice>
        <EntryList entries={timeline} onSelectEntry={onSelectEntry} />
      </div>
    );
  }

  if (turns.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-xs text-gray-500">
        This execution has no recorded steps or waits.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {truncated && (
        <Notice
          icon={Info}
          tone="border-blue-500/30 bg-blue-500/5 text-blue-200/90"
          title="Showing part of the run."
        >
          {truncatedReason || "This execution has more runs than one page returns."}
        </Notice>
      )}

      <div className="flex items-center justify-between px-1">
        <div>
          <h3 className="text-sm font-medium text-white">
            {neverWaited
              ? "Ran straight through"
              : `${turns.length} ${turns.length === 1 ? "turn" : "turns"}`}
          </h3>
          <p className="text-[10px] text-gray-500">
            {neverWaited
              ? "No waits: the execution never stopped to wait for anything."
              : "Each turn is a wait and the work that ran once it was released."}
          </p>
        </div>
        <button
          type="button"
          onClick={toggleAll}
          className="rounded-lg border border-white/10 px-2.5 py-1.5 text-[11px] font-medium text-gray-300 transition-colors hover:border-violet-500/30 hover:text-white"
        >
          {allExpanded ? "Collapse all" : "Expand all"}
        </button>
      </div>

      <div className="space-y-2">
        {turns.map((turn) => (
          <TurnCard
            key={turn.index}
            turn={turn}
            expanded={expanded.has(turn.index)}
            onToggle={() => toggle(turn.index)}
            onSelectEntry={onSelectEntry}
          />
        ))}
      </div>

      <Legend />
    </div>
  );
}
